import { isEmpty } from '@sgme/fp';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { CompactList } from 'components/shared/CompactList';
import type { IntlKey } from 'locales';
import { uniqBy } from 'lodash-es';
import type { ListFieldData, ListFieldMetadata } from 'models/forms/fields';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { camelCaseToWords } from 'utils/string';

export function useToProductTypeLabel(): (code: string) => string {
  const { formatMessage } = useIntl();

  return (code: string) => {
    const key = `field.${code}` as IntlKey;
    const result = formatMessage({ id: key, defaultMessage: camelCaseToWords(code) });
    return result;
  };
}

interface ProductListFieldProps {
  fieldId: string;
  isDisabled?: boolean;
  className?: string;
}

export function ProductListField({ fieldId, isDisabled, className }: ProductListFieldProps) {
  const { availableItems = [], isReadOnly } = useFieldMetadata<ListFieldMetadata>(fieldId)!;
  const isInvalid = useIsFieldInvalid(fieldId);
  const toLabel = useToProductTypeLabel();
  const [value, setValue] = useData<ListFieldData>(fieldId);

  const availableOptions = useMemo(() => {
    const sanitized = uniqBy(availableItems, 'value');
    if (sanitized.length < availableItems.length) {
      throw new Error(
        `ListField<${fieldId}> contains some bad values (duplicate) : ${JSON.stringify(availableItems.map(x => x.value))}`,
      );
    }

    return sanitized.map(({ label, value: val }) => {
      if (isEmpty(label)) {
        const lab = val ?? '';
        return { value: val, label: lab };
      }
      return {
        value: val,
        label: toLabel(label),
      };
    });
  }, [availableItems, toLabel, fieldId]);

  return (
    <CompactList
      id={fieldId}
      className={className}
      availableItems={availableOptions}
      value={value}
      onValueChanged={setValue}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      selectStyle={{ minWidth: '12rem' }}
    />
  );
}

ProductListField.displayName = 'ProductListField';
